import { useEffect, useState } from "react";
import { useNavigate, Link } from "react-router-dom";
import axios from "axios";
import { toast } from "sonner";
import { LogOut, CalendarDays, FileText, BellRing } from "lucide-react";
import { API } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";

const STATUS_OPTIONS = {
  appointments: ["new", "contacted", "confirmed", "completed", "cancelled"],
  quotes: ["new", "quoted", "accepted", "declined", "completed"],
};

const statusColor = (s) =>
  s === "new"
    ? "text-[#E51B23] border-[#E51B23]/50"
    : s === "completed" || s === "accepted" || s === "confirmed"
    ? "text-emerald-400 border-emerald-400/40"
    : s === "cancelled" || s === "declined"
    ? "text-red-400 border-red-400/40"
    : "text-zinc-300 border-zinc-700";

export default function AdminDashboard() {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const [tab, setTab] = useState("appointments");
  const [stats, setStats] = useState(null);
  const [appointments, setAppointments] = useState([]);
  const [quotes, setQuotes] = useState([]);
  const [loadingData, setLoadingData] = useState(true);

  useEffect(() => {
    if (user === false) navigate("/admin/login");
  }, [user, navigate]);

  useEffect(() => {
    if (!user) return;
    const load = async () => {
      try {
        const [s, a, q] = await Promise.all([
          axios.get(`${API}/admin/stats`, { withCredentials: true }),
          axios.get(`${API}/appointments`, { withCredentials: true }),
          axios.get(`${API}/quotes`, { withCredentials: true }),
        ]);
        setStats(s.data);
        setAppointments(a.data);
        setQuotes(q.data);
      } catch {
        toast.error("Could not load dashboard data");
      } finally {
        setLoadingData(false);
      }
    };
    load();
  }, [user]);

  const updateStatus = async (kind, id, status) => {
    try {
      await axios.patch(`${API}/${kind}/${id}`, { status }, { withCredentials: true });
      if (kind === "appointments")
        setAppointments((prev) => prev.map((x) => (x.id === id ? { ...x, status } : x)));
      else setQuotes((prev) => prev.map((x) => (x.id === id ? { ...x, status } : x)));
      toast.success(`Marked as ${status}`);
    } catch {
      toast.error("Status update failed");
    }
  };

  if (!user) {
    return (
      <div className="min-h-screen bg-[#0C0F1C] flex items-center justify-center text-zinc-500 text-sm uppercase tracking-[0.3em]" data-testid="admin-loading">
        Loading...
      </div>
    );
  }

  const rows = tab === "appointments" ? appointments : quotes;

  return (
    <div className="min-h-screen bg-[#0C0F1C] text-zinc-100" data-testid="admin-dashboard">
      <header className="border-b border-zinc-800 bg-[#131A2E] px-6 md:px-10 h-16 flex items-center justify-between sticky top-0 z-40">
        <div className="flex items-baseline gap-3">
          <Link to="/"><img src="/assets/osc-logo.png" alt="OSC Collision Repairs" className="h-9 w-auto" /></Link>
          <span className="text-[10px] uppercase tracking-[0.3em] text-zinc-500">Shop Dashboard</span>
        </div>
        <div className="flex items-center gap-6">
          <span className="hidden sm:block text-xs text-zinc-500">{user.email}</span>
          <button
            data-testid="admin-logout-button"
            onClick={async () => { await logout(); navigate("/admin/login"); }}
            className="flex items-center gap-2 text-xs uppercase tracking-[0.2em] text-zinc-400 hover:text-[#E51B23] transition-colors"
          >
            <LogOut size={14} /> Logout
          </button>
        </div>
      </header>

      <main className="px-6 md:px-10 py-10">
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 mb-10" data-testid="admin-stats">
          {[
            { label: "Appointments", value: stats?.appointments_total, icon: CalendarDays },
            { label: "New Appointments", value: stats?.appointments_new, icon: BellRing, hot: true },
            { label: "Estimate Requests", value: stats?.quotes_total, icon: FileText },
            { label: "New Estimates", value: stats?.quotes_new, icon: BellRing, hot: true },
          ].map((s, i) => (
            <div key={s.label} className="border border-zinc-800 bg-[#131A2E] p-6" data-testid={`stat-card-${i}`}>
              <s.icon size={18} className={s.hot ? "text-[#E51B23]" : "text-zinc-600"} />
              <p className={`font-display text-4xl mt-4 ${s.hot ? "text-[#E51B23]" : "text-zinc-100"}`}>
                {s.value ?? "—"}
              </p>
              <p className="text-[10px] uppercase tracking-[0.25em] text-zinc-500 mt-2">{s.label}</p>
            </div>
          ))}
        </div>

        <div className="flex gap-2 mb-6">
          {["appointments", "quotes"].map((t) => (
            <button
              key={t}
              data-testid={`tab-${t}`}
              onClick={() => setTab(t)}
              className={`px-6 py-3 text-xs uppercase tracking-[0.2em] border transition-colors duration-300 ${
                tab === t
                  ? "border-[#E51B23] text-[#E51B23] bg-[#E51B23]/10"
                  : "border-zinc-800 text-zinc-500 hover:text-zinc-200"
              }`}
            >
              {t === "appointments" ? "Appointments" : "Estimate Requests"}
            </button>
          ))}
        </div>

        {loadingData ? (
          <p className="text-zinc-500 text-sm" data-testid="admin-table-loading">Loading...</p>
        ) : rows.length === 0 ? (
          <div className="border border-zinc-800 bg-[#131A2E] p-16 text-center" data-testid="admin-empty-state">
            <p className="font-display uppercase text-2xl text-zinc-400">Nothing here yet</p>
            <p className="text-zinc-600 text-sm mt-2">
              {tab === "appointments"
                ? "New booking requests from the website will land here."
                : "New estimate requests from the website will land here."}
            </p>
          </div>
        ) : (
          <div className="border border-zinc-800 overflow-x-auto" data-testid="admin-table">
            <table className="w-full text-sm min-w-[760px]">
              <thead>
                <tr className="border-b border-zinc-800 bg-[#131A2E] text-left">
                  {["Date", "Name", "Contact", "Vehicle", tab === "appointments" ? "Service / Date" : "Type / Details", "Status"].map((h) => (
                    <th key={h} className="px-5 py-4 text-[10px] uppercase tracking-[0.25em] text-zinc-500 font-semibold">{h}</th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {rows.map((r) => (
                  <tr key={r.id} className="border-b border-zinc-800/60 hover:bg-[#131A2E] transition-colors" data-testid={`row-${r.id}`}>
                    <td className="px-5 py-4 text-zinc-500 whitespace-nowrap">
                      {r.created_at ? new Date(r.created_at).toLocaleDateString() : "—"}
                    </td>
                    <td className="px-5 py-4 text-zinc-100 font-semibold">{r.name}</td>
                    <td className="px-5 py-4 text-zinc-400">
                      <div>{r.email}</div>
                      <div className="text-zinc-600">{r.phone}</div>
                    </td>
                    <td className="px-5 py-4 text-zinc-300">{r.vehicle}</td>
                    <td className="px-5 py-4 text-zinc-400 max-w-[260px]">
                      {tab === "appointments" ? (
                        <>
                          <div className="text-zinc-200">{r.service}</div>
                          <div className="text-zinc-600 text-xs mt-1">Prefers {r.preferred_date}</div>
                          {r.message && <div className="text-zinc-600 text-xs mt-1 truncate" title={r.message}>{r.message}</div>}
                        </>
                      ) : (
                        <>
                          <div className="text-zinc-200">{r.service_type}</div>
                          <div className="text-zinc-600 text-xs mt-1 truncate" title={r.description}>{r.description}</div>
                        </>
                      )}
                    </td>
                    <td className="px-5 py-4">
                      <select
                        data-testid={`status-select-${r.id}`}
                        value={r.status}
                        onChange={(e) => updateStatus(tab, r.id, e.target.value)}
                        className={`bg-[#0C0F1C] border px-3 py-2 text-xs uppercase tracking-[0.15em] cursor-pointer focus:outline-none focus:border-[#E51B23] ${statusColor(r.status)}`}
                      >
                        {STATUS_OPTIONS[tab].map((s) => (
                          <option key={s} value={s}>{s}</option>
                        ))}
                      </select>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </main>
    </div>
  );
}
